if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Hide the "select your size" warning as soon as the shopper picks a size.
        const variantSelects = document.querySelector(`variant-selects[data-section="${this.dataset.sectionId}"]`);
        if (variantSelects) {
          variantSelects.addEventListener('change', () => this.toggleSizeWarning(false));
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        // Notify-me: a selected but sold-out variant. The button stays visually
        // enabled and clickable, but instead of adding to cart it triggers the
        // back-in-stock flow.
        if (this.submitButton.hasAttribute('data-notify')) {
          document.dispatchEvent(
            new CustomEvent('notify-me:open', {
              detail: { variantId: this.variantIdInput.value },
            })
          );
          return;
        }

        // Truly disabled: no variant chosen ("Select size") or an unavailable
        // option combination. Surface the size warning instead of adding to cart,
        // and bring the variant picker into view — the submit button can be pinned
        // to the bottom of the screen (sticky mobile CTA), leaving the picker off-screen.
        if (this.submitButton.getAttribute('aria-disabled') === 'true') {
          this.toggleSizeWarning(true);
          this.scrollToVariantPicker();
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        // "Notify me" (a selected, sold-out variant) is a real action, not a
        // disabled state: keep it visually enabled but mark it so the submit
        // handler routes the click to the back-in-stock flow instead of cart.
        const isNotifyState = disable && text === window.variantStrings.notifyMe;

        if (disable && !isNotifyState) {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
        }

        this.submitButton.toggleAttribute('data-notify', isNotifyState);

        if (disable) {
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get sizeWarnings() {
        return document.querySelectorAll(`[data-size-warning][data-section="${this.dataset.sectionId}"]`);
      }

      // Bring the variant picker into view. The submit button can be pinned to the
      // bottom of the screen (sticky mobile CTA), so when a shopper taps it without
      // choosing a size we scroll up to the picker (centered, clear of the header).
      scrollToVariantPicker() {
        const picker = document.getElementById('variant-select-options');
        if (picker) {
          picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      // Toggle between hidden/flex (rather than the `hidden` attribute) so the
      // Tailwind display utility wins unambiguously when the warning is shown.
      toggleSizeWarning(show) {
        this.sizeWarnings.forEach((el) => {
          el.classList.toggle('tw:hidden', !show);
          el.classList.toggle('tw:flex', show);
        });
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
